// lib
import { useContext, useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { DataContext } from '@aytm/app/stats/page/question-views/components/questions-mapping/Component'

import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'

import { itemTemplate, valueTemplate, optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/questions-mapping/styles/Component.module.scss'

const Variable = ({ item, position, dndItemsList }) => {
  const { id, data, disabled, dispatch } = useContext(DataContext)

  const [selectedValue, setSelectedValue] = useState(null)

  const changedRef = useRef(false)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const selectedValueRef = useRef(null)
  selectedValueRef.current = selectedValue

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: PageGlobals.WINDOW_UID,
      remove: () =>
        dispatch({
          type: 'DELETE_VARIABLE',
          data: position - 1
        })
    }
  }, [item, position, id, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-variable-${PageGlobals.WINDOW_UID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-variable-${PageGlobals.WINDOW_UID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const onSelectedChange = (value = null, _values) => {
    const group = data.filters.find(v => v.items.some(v => v.key == value)) || null
    if (null !== group && group.disabled) return

    const option = null === group ? null : group.items.find(v => v.key == value) || null
    if (null !== option && option.disabled) return

    setSelectedValue(value)

    changedRef.current = true

    const options =
      null === option || option.options == undefined
        ? []
        : option.options.filter(
            _v => null !== _v.pattern && _v.pattern != undefined && 0 < _v.pattern.length && !_v.other
          )

    dispatch({
      type: 'UPDATE_VARIABLE',
      data: {
        item: {
          ...item,
          text: null === option ? '' : option.text,
          pattern: null === option ? '' : value,
          rules:
            null === option || option.options == undefined
              ? []
              : option.options.map(v =>
                  v.other
                    ? 0 < options.length
                      ? `not (${options.map(_v => `(${_v.pattern})`).join(' or ')})`
                      : ''
                    : v.pattern
                )
        },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_VARIABLE',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_VARIABLE',
      data: position - 1
    })
  }

  const onHandleClick = event => {
    if (2 != event.detail) return

    dispatch({
      type: 'ADD_VARIABLE',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  }

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    const patternRefs = RegExp(/^(vq\d+)/i).test(item.pattern) ? [item.pattern] : []

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `v${position}`,
        refs: [patternRefs, [], []]
      }
    })
  }, [item.pattern, position, dispatch])

  useEffect(() => {
    if (changedRef.current) {
      changedRef.current = false
      return
    }

    const option =
      data.filters
        .flatMap(v => v.items)
        .find(v => String(v.value || v.key).toLowerCase() == item.pattern.toLowerCase()) || null

    if (null === option) {
      setSelectedValue(null)
    } else {
      setSelectedValue(option.key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.pattern])

  useEffect(
    () => {
      if (null !== selectedValueRef.current) {
        const option =
          data.filters
            .flatMap(v => v.items)
            .find(v => String(v.value || v.key).toLowerCase() == selectedValueRef.current.toLowerCase()) || null

        if (null === option || option.options == undefined) setSelectedValue(null)
        else {
          const options = option.options.filter(
            _v => null !== _v.pattern && _v.pattern != undefined && 0 < _v.pattern.length && !_v.other
          )

          dispatch({
            type: 'UPDATE_VARIABLE',
            data: {
              item: {
                ...item,
                rules: option.options.map(v =>
                  v.other
                    ? 0 < options.length
                      ? `not (${options.map(_v => `(${_v.pattern})`).join(' or ')})`
                      : ''
                    : v.pattern
                )
              },
              position: position - 1
            }
          })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.filters]
  )

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']}`}>
      {disabled ? null : (
        <span className={style.delete} aria-label="Delete variable" {...pressable(onDeleteClick)}>
          <i className="icon-aytmfd-x-v2" />
        </span>
      )}
      {/* keyed by position: the list's drag handlers rewrite this cell's text outside React, so a new
          position mounts a fresh node rather than updating text that is no longer on screen */}
      <div key={position} className={style.index}>
        {position}.
      </div>
      <div className={`${style.group} ${style['group-av']}${disabled ? ' ' + style['group-av-disabled'] : ''} p-col`}>
        <div className="p-fluid p-col-8">
          <Dropdown
            className="p-dropdown-rounded p-composite-right"
            placeholder="Select..."
            value={selectedValue}
            options={data.filters}
            optionValue="key"
            optionLabel="text"
            optionDisabled="disabled"
            optionGroupLabel="text"
            optionGroupChildren="items"
            optionGroupTemplate={optionGroupTemplate}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            filter
            filterBy="search,text"
            filterPlaceholder="Search items"
            resetFilterOnHide
            filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            disabled={disabled}
            onChange={disabled ? null : event => onSelectedChange(event.value)}
          />
        </div>
        <div className="p-fluid p-col">
          <InputText
            className={classNames('p-inputtext-rounded', {
              'p-composite': !disabled,
              'p-composite-left': disabled
            })}
            placeholder="Please type a label..."
            value={decodeText(item.text)}
            maxLength={120}
            disabled={disabled}
            onChange={disabled ? null : onLabelChange}
          />
        </div>
      </div>
      {disabled ? null : <span ref={dndListItemHandleRef} className={style.handle} onClick={onHandleClick} />}
    </div>
  )
}

Variable.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dndItemsList: PropTypes.any
}

Variable.displayName = 'Variable'

export default Variable
